import { motion } from 'framer-motion'
import { forwardRef, useRef, type HTMLAttributes } from 'react'
import { BRANDS, BrandGlyph } from './brands'
import { AnimatedBeam } from './ui/animated-beam'
import YaslanLogo from './YaslanLogo'
import { useT } from '../i18n/LocaleContext'

const platforms = [
  { id: 'shopify' },
  { id: 'woocommerce' },
  { id: 'amazon' },
  { id: 'prestashop' },
  { id: 'odoo' },
] as const

const carriersOut = [
  { id: 'dhl', file: 'DHL.svg', name: 'DHL' },
  { id: 'ups', file: 'ups.svg', name: 'UPS' },
  { id: 'fedex', file: 'Fedex.svg', name: 'FedEx' },
  { id: 'bpost', file: 'Bpost.svg', name: 'bpost' },
  { id: 'gls', file: 'Gls.svg', name: 'GLS' },
] as const

type CircleSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZE_MAP: Record<CircleSize, string> = {
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-20',
  xl: 'size-28',
}

const Circle = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { size?: CircleSize }>(
  ({ className = '', children, size = 'md', ...props }, ref) => {
    return (
      <div
        ref={ref}
        {...props}
        className={`z-10 flex ${SIZE_MAP[size]} items-center justify-center rounded-full border border-ink-100 bg-white shadow-[0_18px_40px_-18px_rgba(59,93,255,0.25),0_2px_8px_rgba(14,14,20,0.05)] ${className}`}
      >
        {children}
      </div>
    )
  }
)
Circle.displayName = 'Circle'

function PlatformCircle({ id }: { id: string }) {
  const b = BRANDS[id]
  if (!b) return null
  if (b.type === 'icon') {
    return <BrandGlyph id={id} size={32} />
  }
  return (
    <div
      className="rounded px-2 py-1 text-[11px] font-bold tracking-tight"
      style={{ background: b.bg, color: b.color }}
    >
      {b.wordmark}
    </div>
  )
}

function CarrierLogo({ file, name }: { file: string; name: string }) {
  return (
    <img
      src={`/carriers/${encodeURI(file)}`}
      alt={name}
      draggable={false}
      className="max-h-9 max-w-[60%] w-auto object-contain"
    />
  )
}

function HubDiagram() {
  const { t } = useT()
  const containerRef = useRef<HTMLDivElement>(null)
  const hubRef = useRef<HTMLDivElement>(null)

  const p0 = useRef<HTMLDivElement>(null)
  const p1 = useRef<HTMLDivElement>(null)
  const p2 = useRef<HTMLDivElement>(null)
  const p3 = useRef<HTMLDivElement>(null)
  const p4 = useRef<HTMLDivElement>(null)
  const platformRefs = [p0, p1, p2, p3, p4]

  const c0 = useRef<HTMLDivElement>(null)
  const c1 = useRef<HTMLDivElement>(null)
  const c2 = useRef<HTMLDivElement>(null)
  const c3 = useRef<HTMLDivElement>(null)
  const c4 = useRef<HTMLDivElement>(null)
  const carrierRefs = [c0, c1, c2, c3, c4]

  return (
    // Double-Bezel : plateau gris autour de la plaque blanche du diagramme,
    // rayons concentriques — même matière que les cartes Services.
    // containerRef reste sur la plaque interne : c'est elle qui sert de
    // référentiel de coordonnées aux beams.
    <div className="rounded-[1.75rem] bg-ink-950/[0.04] p-1.5 ring-1 ring-ink-950/[0.06]">
    <div
      ref={containerRef}
      className="relative flex h-[500px] w-full items-center justify-between overflow-hidden rounded-[calc(1.75rem-0.375rem)] border border-ink-950/[0.05] bg-gradient-to-b from-white via-white to-accent-50/30 p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    >
      {/* Subtle dot grid */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.14) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          maskImage:
            'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      {/* Soft accent halo behind hub */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(59,93,255,0.18), rgba(59,93,255,0) 60%)',
          filter: 'blur(40px)',
        }}
      />

      {/* Left column: platforms */}
      <div className="relative z-10 flex flex-col justify-between gap-4 h-full py-4">
        {platforms.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, x: -16, scale: 0.9 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.06, y: -2 }}
          >
            <Circle ref={platformRefs[i]} size="md" title={BRANDS[p.id]?.name}>
              <PlatformCircle id={p.id} />
            </Circle>
          </motion.div>
        ))}
      </div>

      {/* Center: Yaslan hub */}
      <div className="relative z-10 flex flex-col items-center gap-3">
        <motion.div
          initial={{ scale: 0.85, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          {/* Outer breathing halo — resserré et adouci pour que le wordmark
              du hub reste net (l'ancien bloom noyait le logo) */}
          <motion.div
            aria-hidden
            animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute -inset-9 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(59,93,255,0.3), rgba(59,93,255,0) 70%)',
              filter: 'blur(18px)',
            }}
          />

          {/* Pulsing rings (3 layered) */}
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              aria-hidden
              animate={{
                scale: [1, 1.6, 1.6],
                opacity: [0.6, 0, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeOut',
                delay: i * 1,
              }}
              className="absolute inset-0 rounded-full border-2 border-accent-400/50"
            />
          ))}

          {/* Static inner ring */}
          <div className="absolute -inset-3 rounded-full border border-accent-300/40" />
          <div className="absolute -inset-6 rounded-full border border-accent-200/30" />

          {/* Orbiting particles */}
          <motion.div
            aria-hidden
            animate={{ rotate: 360 }}
            transition={{ duration: 16, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-8 rounded-full"
          >
            <span className="absolute left-1/2 top-0 -translate-x-1/2 w-2 h-2 rounded-full bg-accent-500 shadow-[0_0_12px_rgba(59,93,255,0.8)]" />
          </motion.div>
          <motion.div
            aria-hidden
            animate={{ rotate: -360 }}
            transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-12 rounded-full"
          >
            <span className="absolute left-1/2 top-0 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-accent-400 shadow-[0_0_10px_rgba(59,93,255,0.7)]" />
            <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-accent-300 shadow-[0_0_10px_rgba(59,93,255,0.5)]" />
          </motion.div>

          {/* Hub itself */}
          <Circle
            ref={hubRef}
            size="xl"
            className="!bg-gradient-to-br !from-accent-700 !via-accent-600 !to-accent-700 !border-accent-700 shadow-[0_24px_55px_-18px_rgba(59,93,255,0.55)] relative overflow-hidden"
          >
            {/* inner glossy ring */}
            <span className="absolute inset-0 rounded-full ring-1 ring-white/20 pointer-events-none" />
            {/* shimmer sweep */}
            <motion.span
              aria-hidden
              animate={{ x: ['-110%', '110%'] }}
              transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.6 }}
              className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none"
            />
            <YaslanLogo variant="white" height={36} withBar={false} />
          </Circle>
        </motion.div>
        <motion.span
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-[10px] font-semibold uppercase tracking-[0.25em] text-accent-700 mt-2"
        >
          {t('integrations.hubLabel')}
        </motion.span>
      </div>

      {/* Right column: carriers (real SVG logos) */}
      <div className="relative z-10 flex flex-col justify-between gap-4 h-full py-4">
        {carriersOut.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, x: 16, scale: 0.9 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.55, delay: 0.25 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.06, y: -2 }}
          >
            <Circle ref={carrierRefs[i]} size="md" title={c.name}>
              <CarrierLogo file={c.file} name={c.name} />
            </Circle>
          </motion.div>
        ))}
      </div>

      {/* Beams: platforms → hub. Tracé de base légèrement bleuté et plus
          présent : les routes se lisent même quand le pulse ne passe pas. */}
      {platformRefs.map((r, i) => (
        <AnimatedBeam
          key={`in-${i}`}
          containerRef={containerRef}
          fromRef={r}
          toRef={hubRef}
          curvature={-12 + i * 5}
          duration={3 + i * 0.25}
          delay={i * 0.35}
          pathColor="#c7d1f2"
          pathOpacity={0.5}
          gradientStartColor="#5e84ff"
          gradientStopColor="#3b5dff"
        />
      ))}

      {/* Beams: hub → carriers */}
      {carrierRefs.map((r, i) => (
        <AnimatedBeam
          key={`out-${i}`}
          containerRef={containerRef}
          fromRef={hubRef}
          toRef={r}
          curvature={-12 + i * 5}
          duration={3.2 + i * 0.25}
          delay={1.2 + i * 0.35}
          pathColor="#c7d1f2"
          pathOpacity={0.5}
          gradientStartColor="#3b5dff"
          gradientStopColor="#8fb0ff"
        />
      ))}
    </div>
    </div>
  )
}

/* ---- New benefits below the diagram (no repeats with rest of site) ---- */

const benefits = [
  {
    key: 'noCode',
    titleKey: 'integrations.benefitNoCodeTitle',
    descKey: 'integrations.benefitNoCodeDesc',
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 18l6-6-6-6M8 6l-6 6 6 6M14 4l-4 16" />
      </svg>
    ),
  },
  {
    key: 'failover',
    titleKey: 'integrations.benefitFailoverTitle',
    descKey: 'integrations.benefitFailoverDesc',
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12a9 9 0 1 1-3.5-7.1" />
        <path d="M21 4v5h-5" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    key: 'webhooks',
    titleKey: 'integrations.benefitWebhooksTitle',
    descKey: 'integrations.benefitWebhooksDesc',
    Icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a3 3 0 0 1 0 6h-1" />
        <path d="M2 8h16v6a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
        <path d="M6 1v3M10 1v3M14 1v3" />
      </svg>
    ),
  },
] as const

/* -------------------- Section -------------------- */

export default function Integrations() {
  const { t } = useT()
  return (
    <section id="integrations" className="relative isolate py-28 overflow-hidden">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-40"
          style={{
            background: 'linear-gradient(to bottom, rgba(59,93,255,0.04), transparent)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.28]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse at center, black 25%, transparent 80%)',
          }}
        />
        <motion.div
          animate={{ opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute left-[10%] top-1/3 w-[480px] h-[480px] rounded-full"
          style={{
            background:
              'radial-gradient(ellipse, rgba(59,93,255,0.16), rgba(59,93,255,0.04) 45%, transparent 75%)',
            filter: 'blur(60px)',
          }}
        />
        <div
          className="absolute inset-x-0 -bottom-1 h-32"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(14,14,20,0.03))',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-14 mx-auto lg:mx-0 text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('integrations.badgeLabel')}</span>
            <span className="text-accent-400">·</span>
            <span className="text-accent-700">{t('integrations.badgeTagline')}</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(2rem,4.5vw,3.25rem)] leading-[1.05]"
          >
            {t('integrations.headlinePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('integrations.headlinePart2')}
            </span>
            <br />
            {t('integrations.headlinePart3')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('integrations.headlinePart4')}
            </span>
            .
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 text-ink-600 text-lg leading-relaxed"
          >
            {t('integrations.subhead')}
          </motion.p>
        </div>

        <HubDiagram />

        {/* New benefits — Double-Bezel, même matière que les cartes Services */}
        <div className="mt-11 grid sm:grid-cols-3 gap-5">
          {benefits.map((b, i) => (
            <motion.div
              key={b.key}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -3 }}
              className="group h-full rounded-[1.25rem] bg-ink-950/[0.04] p-1.5 ring-1 ring-ink-950/[0.06] transition-shadow duration-500 hover:shadow-[0_26px_60px_-28px_rgba(36,47,214,0.35)]"
            >
              <div className="flex h-full flex-col rounded-[calc(1.25rem-0.375rem)] border border-ink-950/[0.05] bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 rounded-lg bg-gradient-to-br from-accent-600 to-accent-700 text-white flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]">
                    <span className="absolute inset-0 rounded-lg ring-1 ring-white/20" />
                    <b.Icon />
                  </div>
                  <p className="text-[15px] font-semibold text-ink-950 tracking-tight">{t(b.titleKey)}</p>
                </div>
                <p className="mt-3 text-sm text-ink-600 leading-relaxed">{t(b.descKey)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
